import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocalizationScopeDirective, LocalizePipe } from '@capital-access/common/localization';
import { NotificationsDrawerActions } from '../../+store/notifications-drawer.actions';
import { selectExpandedSections } from '../../+store/notifications-drawer.selectors';
import { NotificationTypePageState } from '../../+store/notifications-drawer.state';
import {
  NOTIFICATION_TYPE_CONFIGS,
  NOTIFICATION_TYPE_LABEL_KEYS,
  NOTIFICATIONS_LOCALIZATION_SCOPE
} from '../../constants/notifications-drawer.constants';
import { NotificationByTypeDto } from '../../models/notifications-grouped.model';
import { NotificationItemRendererComponent } from '../notification-item-renderer/notification-item-renderer.component';

@Component({
  selector: 'ca-notification-type-list',
  templateUrl: './notification-type-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LocalizationScopeDirective, LocalizePipe, NotificationItemRendererComponent]
})
export class NotificationTypeListComponent {
  private store = inject(Store);

  notificationsByType = input.required<NotificationByTypeDto>();
  typePageState = input<NotificationTypePageState | undefined>();

  loadMore = output<string>();
  markAsRead = output<{ notificationId: number; typeCode: string }>();
  delete = output<{ notificationId: number; typeCode: string }>();

  readonly localizationScope = NOTIFICATIONS_LOCALIZATION_SCOPE;

  private readonly expandedSectionsMap = this.store.selectSignal(selectExpandedSections);
  // Tracks whether the user has clicked Show More for this type.
  // Reset to false when the component is recreated (e.g. after a full drawer reload).
  private readonly showingAll = signal(false);

  readonly isExpanded = computed(() => !!this.expandedSectionsMap()[this.notificationsByType().notificationTypeCode]);

  readonly typeLabelKey = computed(
    () =>
      NOTIFICATION_TYPE_LABEL_KEYS[this.notificationsByType().notificationTypeCode] ??
      this.notificationsByType().notificationTypeCode
  );

  readonly initialLimit = computed(
    () => NOTIFICATION_TYPE_CONFIGS[this.notificationsByType().notificationTypeCode]?.initialLimit ?? 3
  );

  // When collapsed the content div is not rendered; this computed drives what
  // shows inside that div when it IS rendered (expanded).
  readonly displayedNotifications = computed(() => {
    const all = this.notificationsByType().notifications;
    return this.showingAll() ? all : all.slice(0, this.initialLimit());
  });

  readonly canToggle = computed(() => this.notificationsByType().totalCount > this.initialLimit());

  private readonly serverHasMore = computed(
    () => this.notificationsByType().totalCount > this.notificationsByType().notifications.length
  );

  private readonly hasLoadedExtra = computed(
    () => this.notificationsByType().notifications.length > this.initialLimit()
  );

  // Show More: server has more pages, OR we have extra items loaded but are
  // currently showing only the initialLimit (show-less mode).
  readonly canShowMore = computed(() => this.serverHasMore() || (!this.showingAll() && this.hasLoadedExtra()));

  // Show Less: we are in "show all" mode and there is extra loaded data to collapse.
  readonly canShowLess = computed(() => this.showingAll() && this.hasLoadedExtra());

  readonly isLoadingMore = computed(() => this.typePageState()?.loadingMore ?? false);

  toggleExpanded(): void {
    this.store.dispatch(
      NotificationsDrawerActions.toggleSectionExpansion({
        typeCode: this.notificationsByType().notificationTypeCode
      })
    );
  }

  onShowMore(): void {
    this.showingAll.set(true);
    // Only hit the server if there are actually more pages to fetch.
    if (this.serverHasMore()) {
      this.loadMore.emit(this.notificationsByType().notificationTypeCode);
    }
  }

  onShowLess(): void {
    this.showingAll.set(false);
  }

  onMarkAsRead(notificationId: number): void {
    this.markAsRead.emit({
      notificationId,
      typeCode: this.notificationsByType().notificationTypeCode
    });
  }

  onDelete(notificationId: number): void {
    this.delete.emit({
      notificationId,
      typeCode: this.notificationsByType().notificationTypeCode
    });
  }
}
